<script setup>
import { computed, useAttrs } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: options =>
      options.every(
        opt => typeof opt === 'object' && 'value' in opt && 'label' in opt
      ),
  },
  groups: {
    type: Array,
    default: () => [],
    validator: groups =>
      groups.every(
        group =>
          'label' in group &&
          Array.isArray(group.options) &&
          group.options.every(opt => 'value' in opt && 'label' in opt)
      ),
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  // `sm` tem 32px, a altura do Input `sm`; o padrão segue como era.
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
});

// Como o Checkbox: o que descreve o controle (id, name, aria-*, data-*, @change) vai para o
// <select>, que é o que o leitor de tela anuncia e o que se escolhe; class e style ficam na <div>,
// que é quem ocupa o espaço.
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const daCaixa = computed(() => ({ class: attrs.class, style: attrs.style }));
const doSelect = computed(() =>
  Object.fromEntries(
    Object.entries(attrs).filter(
      ([chave]) => !['class', 'style'].includes(chave)
    )
  )
);

const modelValue = defineModel({
  type: [String, Number, Boolean],
  default: '',
});
</script>

<template>
  <div class="w-fit relative" v-bind="daCaixa">
    <select
      v-bind="doSelect"
      v-model="modelValue"
      :disabled="disabled"
      class="appearance-none bg-none rounded-lg border-0 outline-1 outline -outline-offset-1 transition-all duration-200 bg-n-surface-1 !mb-0 px-3 pr-10 text-sm"
      :class="{
        'py-2': size === 'md',
        'h-8 py-0': size === 'sm',
        'outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand':
          !error && !disabled,
        'outline-n-ruby-8 hover:outline-n-ruby-9 focus:outline-n-ruby-9':
          error && !disabled,
        'outline-n-weak bg-n-slate-2 cursor-not-allowed opacity-60': disabled,
      }"
    >
      <option v-if="placeholder" value="" disabled>
        {{ placeholder }}
      </option>
      <template v-if="groups.length">
        <optgroup
          v-for="group in groups"
          :key="group.label"
          :label="group.label"
        >
          <option
            v-for="option in group.options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            {{ option.label }}
          </option>
        </optgroup>
      </template>
      <template v-else>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </template>
    </select>
    <div
      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
    >
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 text-n-slate-11"
        :class="{ 'opacity-50': disabled }"
      />
    </div>
  </div>
</template>
